import { useState } from 'react'
import { PieChart } from '@mui/x-charts'
import { useCookies } from 'react-cookie'

import environment from 'environment'

import ManaSymbol from 'components/misc/customIcons/ManaSymbol'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Icon from 'components/elements/Icon'

import { ColorAbr } from 'services/apiTypes/card.types'

import { useAppSelector } from 'app-redux/hooks'
import { getCastingCostColors, getDeckColorIdent, getManaProductionData } from 'utils/mtgColor.utils'
import { CardSelectors } from 'utils/cardSelection'
import { CardType, EDH_FORMATS } from 'types/deck'
import { handleHighlight, handleMultiSelect } from 'app-redux/active/actions/multiSelectors'
import { COLOR_MAP, COLOR_OBJECT } from 'components/deckPage/charts/charts.const'

const BAR_WIDTH = 125

import { expirationDurations } from 'services/cookie.service'

import styles from './colorBarChart.module.scss'

const ColorBarChart = () => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const categories = useAppSelector(state => state.deck.categories)
  const format = useAppSelector(state => state.deck.format)

  const deck = Object.values(cardMap).filter(card => {
    const primaryCategoryName = card.categories[0]
    const category = categories[primaryCategoryName]

    if (primaryCategoryName === 'Sideboard' || !category?.includedInDeck) return false

    return true
  })

  const isCommanderFormat = EDH_FORMATS.includes(format)
  const deckColorIdentity: ColorAbr[] = getDeckColorIdent(deck, categories, isCommanderFormat)

  const [landsOnly, setLandsOnly] = useState(false)
  const [showOutOfIdentity, setShowOutOfIdentity] = useState(!isCommanderFormat)
  const [includedColorlessInPie, setIncludedColorlessInPie] = useState(false)

  const [{ cachedPieChart = 'false' }, setCookies] = useCookies(['cachedPieChart'])

  const handleSelectionChoice = (e: React.MouseEvent, cards: CardType[]) => {
    const modKeyHeld = environment.getOnMac() ? e.metaKey : e.ctrlKey

    return modKeyHeld ? handleMultiSelect(cards) : handleHighlight(cards)
  }

  const handleSelectCost = (e: React.MouseEvent, colorAbr: ColorAbr) =>
    handleSelectionChoice(e, CardSelectors.cost(colorAbr))
  const handleSelectProducers = (e: React.MouseEvent, colorAbr: ColorAbr) =>
    handleSelectionChoice(e, CardSelectors.producers(colorAbr))

  const handleOnPieChartClick = (e: React.MouseEvent, item: any) => {
    const { colorAbr, type } = item

    if (type === 'cost') handleSelectCost(e, colorAbr)
    if (type === 'production') handleSelectProducers(e, colorAbr)
  }

  const { colors: castingCostColorData, totalPips: totalCostPips } = getCastingCostColors(deck, categories)
  const { colors: manaProductionData, totalPips: totalProductionPips } = getManaProductionData(
    deck,
    categories,
    landsOnly,
    showOutOfIdentity ? undefined : deckColorIdentity,
  )

  let maxBarPercent = 0

  const jointColorData = Object.keys(castingCostColorData).reduce(
    (acc, color) => {
      const cost = castingCostColorData[color]
      const production = manaProductionData[color]

      const costPercent = cost / totalCostPips
      const productionPercent = production / totalProductionPips

      if (maxBarPercent < costPercent) maxBarPercent = costPercent
      if (maxBarPercent < productionPercent) maxBarPercent = productionPercent

      const style: React.CSSProperties = {
        opacity: cost > 0 ? 1 : 0.35,
        display: cost < 1 && production < 1 ? 'none' : undefined,
      }

      acc.push({
        style,
        color: color as ColorAbr,
        label: (
          <>
            <ManaSymbol symbol={color} />
            <span>Cost</span>
          </>
        ),
        count: cost,
        percent: costPercent,
        onBarClick: e => handleSelectCost(e, color as ColorAbr),
      })

      acc.push({
        style,
        color: color as ColorAbr,
        label: (
          <>
            <ManaSymbol symbol={color} />
            <span>Prod</span>
          </>
        ),
        count: production,
        percent: productionPercent,
        onBarClick: e => handleSelectProducers(e, color as ColorAbr),
      })

      return acc
    },
    [] as {
      color: ColorAbr
      label: React.ReactNode
      count: number
      percent: number
      onBarClick: (e: React.MouseEvent) => void
      style: React.CSSProperties
    }[],
  )

  const castingCostPiChartData = Object.keys(castingCostColorData)
    .filter(colorAbr => castingCostColorData[colorAbr] > 0)
    .map(colorAbr => {
      const count = castingCostColorData[colorAbr]

      return {
        label: `${COLOR_MAP[colorAbr]} pips`,
        value: count,
        color: COLOR_OBJECT[COLOR_MAP[colorAbr]],
        colorAbr,
        type: 'cost',
      }
    })

  const productionPiChartData = Object.keys(manaProductionData)
    .filter(colorAbr => {
      if (!includedColorlessInPie && colorAbr === 'C') return false

      return manaProductionData[colorAbr] > 0
    })
    .map(colorAbr => {
      const count = manaProductionData[colorAbr]

      return {
        label: `${COLOR_MAP[colorAbr]} pips`,
        value: count,
        color: COLOR_OBJECT[COLOR_MAP[colorAbr]],
        colorAbr,
        type: 'production',
      }
    })

  const viewingPieChart = `${cachedPieChart}` === 'true'

  return (
    <div className={styles.miniColorChart}>
      <div className={styles.header}>
        <label>Color Cost & Production</label>
        <ArchidektDropdown
          menuClassName={styles.productionOptionsMenu}
          options={[
            {
              type: 'checkbox',
              checked: showOutOfIdentity,
              label: 'Show out of identity colors',
              onChange: () => setShowOutOfIdentity(!showOutOfIdentity),
            },
            {
              type: 'checkbox',
              checked: landsOnly,
              label: 'Use lands only for production',
              onChange: () => setLandsOnly(!landsOnly),
            },
            { type: 'spacer' },
            {
              type: 'checkbox',
              checked: viewingPieChart,
              label: 'Show as pie chart',
              onChange: () =>
                setCookies('cachedPieChart', `${!viewingPieChart}`, {
                  maxAge: expirationDurations.year,
                }),
            },
            {
              type: 'checkbox',
              checked: includedColorlessInPie,
              label: 'Include colorless in pie chart',
              onChange: () => setIncludedColorlessInPie(!includedColorlessInPie),
            },
          ]}>
          <button className={styles.productionOptionsTrigger}>
            <Icon name="ellipsis horizontal" />
          </button>
        </ArchidektDropdown>
      </div>

      {viewingPieChart && (
        <>
          <div className={styles.piChartLabels}>
            <label>Outer: Cost</label>
            <label>Inner: Production</label>
          </div>

          <div className={styles.piChartWrapper}>
            <PieChart
              skipAnimation
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              series={[
                {
                  innerRadius: 0,
                  outerRadius: 70,
                  data: productionPiChartData,
                },
                {
                  innerRadius: 80,
                  outerRadius: 100,
                  data: castingCostPiChartData,
                },
              ]}
              onItemClick={(event, _, item) => handleOnPieChartClick(event, item)}
              width={244}
              height={244}
              hideLegend
              sx={{
                stroke: 'green',
                strokeWidth: 2,
              }}
            />
          </div>
        </>
      )}

      {!viewingPieChart && (
        <div className={styles.verticalChart}>
          {jointColorData.map(({ color, label, count, percent, onBarClick, style }, index) => {
            const backgroundColor = COLOR_OBJECT[COLOR_MAP[color]]

            return (
              <button onClick={e => onBarClick && onBarClick(e)} key={index} style={style}>
                <label className={styles.label}>{label}</label>
                <label className={styles.quantity}>{Math.round(percent * 100)}%</label>
                {count > 0 && (
                  <span
                    title={`${count} pips`}
                    className={styles.bar}
                    style={{ width: `${(percent / maxBarPercent) * BAR_WIDTH}px`, backgroundColor }}
                  />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ColorBarChart
